/**
 * Typing an action instead of clicking one — `/look at the gull`.
 *
 * **This is a client affordance and nothing more.** It fills exactly the fields
 * the verb button fills, and the token then renders in the composer where the
 * player can see and remove it before sending. The server still receives an
 * explicit verb; `text` stays elaboration and is never parsed for intent, which
 * is not fussiness — the verb sets the attempt *tier*, and the tier carries
 * visibility and time cost. A server that inferred `hide` from "hide behind the
 * crates" would let a player believe they had acted secretly when they had not.
 *
 * **A sigil rather than the first word.** Matching a bare first word converts
 * table talk into actions: "Look, we should just leave" would become a `look`
 * that costs time and reaches the director, and the comma heuristic that
 * separates it from "Look at the gull" is the kind of rule that is wrong one
 * time in twenty and silently. Concealed verbs make that worse — "hide the body
 * from Ilra" firing `hide` changes who can see the result. The slash is one
 * character and has no such failure.
 */

import type { VerbDescriptor } from '@portal/shared';

export interface SlashMatch {
  readonly verb: VerbDescriptor;
  /** What is left once the command is taken off — the elaboration. */
  readonly rest: string;
}

/**
 * Reads `/verb the rest` once the verb is complete.
 *
 * **Only once a separator has been typed.** `/look` on its own is still being
 * written — it could become `/lookout` — so it stays as text and the caller
 * offers suggestions instead. The moment a space follows, the intent is
 * settled.
 *
 * Returns `undefined` for anything that is not a command, which is most of what
 * anybody types.
 */
export function readSlashCommand(
  draft: string,
  verbs: readonly VerbDescriptor[],
): SlashMatch | undefined {
  if (!draft.startsWith('/')) return undefined;

  const separator = draft.search(/\s/);
  if (separator < 0) return undefined;

  const typed = draft.slice(1, separator).toLowerCase();
  if (typed === '') return undefined;

  // Underscores are how the closed set spells a two-word verb, and a player
  // typing the space instead is not making a mistake worth refusing.
  const wanted = typed.replace(/[\s-]+/g, '_');
  const verb = verbs.find((candidate) => candidate.verb === wanted);
  if (!verb) return undefined;

  return { verb, rest: draft.slice(separator + 1).trimStart() };
}

/**
 * The verbs a half-typed command could still become.
 *
 * Doubles as discovery: thirteen of the nineteen live behind a "More" toggle,
 * and a player who has never opened it has no idea `appraise` exists.
 * Empty for anything that is not a bare `/word` still being typed, so an
 * ordinary sentence never raises a menu.
 */
export function slashSuggestions(
  draft: string,
  verbs: readonly VerbDescriptor[],
): readonly VerbDescriptor[] {
  if (!draft.startsWith('/') || /\s/.test(draft)) return [];
  const typed = draft.slice(1).toLowerCase().replace(/[\s-]+/g, '_');
  return verbs.filter((verb) => verb.verb.startsWith(typed));
}
